import { AvatarDropdown, AvatarName } from "@/components";
import {
  PageLoading,
  type Settings as LayoutSettings,
} from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history } from "@umijs/max";
import { message as antdMessage } from "antd";
import defaultSettings from "../config/defaultSettings";
import type { IConfig as ExamineeConfig } from "../config/examinee-config";
import defaultExamineeConfig from "../config/examinee-config";
import type { IConfig as InvigilatorConfig } from "../config/invigilator-config";
import defaultInvigilatorConfig from "../config/invigilator-config";
import { getConfig, getRoutes, getUsersInfo } from "./api/user";
import { errorConfig } from "./requestErrorConfig";
import { clearUserInfo, getUserInfo, setMinioUrl } from "./utils/storage";
// 配置静态方法，避免 context 警告
antdMessage.config({
  top: 100,
  duration: 2,
  maxCount: 3,
});

const message = antdMessage;

const isDev = process.env.NODE_ENV === "development";
const loginPath = "/user/login";

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
  fetchUserInfo?: () => Promise<any | undefined>;
  examineeConfig?: ExamineeConfig;
  invigilatorConfig?: InvigilatorConfig;
}> {
  const fetchUserInfo = async () => {
    try {
      let data;
      const userInfo = getUserInfo();
      if (userInfo) {
        // 获取系统配置
        const systemConfig = await getConfig();
        // 存储域名
        setMinioUrl(systemConfig.data.sourceDomain);

        //  先加载配置（从后端获取动态配置并合并到默认配置）
        let examineeConfig: ExamineeConfig = defaultExamineeConfig;
        let invigilatorConfig: InvigilatorConfig = defaultInvigilatorConfig;

        examineeConfig = {
          ...defaultExamineeConfig,
          aliyunUid: systemConfig.data.aliyunUid,
          pagePath: `${window.location.origin}/exam-port`, // 学生考试扫描二维码的跳转的路径
          appServer: {
            origin: API_BASE_URL, // 配置 APPServer 服务域名，
            apiPrefixPath: API_PREFIX_PATH, // 配置api接口路径前缀
          },
        };
        invigilatorConfig = {
          ...defaultInvigilatorConfig,
          aliyunUid: "",
          appServer: {
            origin: API_BASE_URL, // 配置 APPServer 服务域名
            apiPrefixPath: API_PREFIX_PATH, // 配置api接口路径前缀
          },
        };

        //  获取路由信息
        const routers = await getRoutes({ roleId: userInfo.roleId });

        // 3 获取用户详细信息
        const userDetail = await getUsersInfo();

        //  合并所有信息（包括配置）
        data = {
          ...userInfo,
          ...userDetail.data,
          routers: routers.data,
          minioUrl: systemConfig.data.sourceDomain,
          examineeConfig, // ← 配置也放到这里
          invigilatorConfig, // ← 配置也放到这里
        };
      }
      return data;
    } catch (_error) {
      // message.error("登录状态已过期,请重新登录");
      clearUserInfo(); // 清空存储
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, "/user/register", "/user/register-result"].includes(
      location.pathname
    )
  ) {
    const currentUser = await fetchUserInfo();
    // 获取不到用户信息，则跳转到登录页
    if (!currentUser) {
      // 获取当前页面信息用于重定向
      const { search, pathname } = location;
      const urlParams = new URL(window.location.href).searchParams;
      const searchParams = new URLSearchParams({
        redirect: pathname + search,
      });
      // 检查是否已有 redirect 参数
      const redirect = urlParams.get("redirect");
      // Note: There may be security issues, please note
      message.error("登录状态已过期,请重新登录");
      if (!redirect) {
        history.replace({
          pathname: loginPath,
          search: searchParams.toString(),
        });
      } else {
        history.push(loginPath);
      }
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      // 从 currentUser 中提取配置
      examineeConfig: currentUser?.examineeConfig || defaultExamineeConfig,
      invigilatorConfig:
        currentUser?.invigilatorConfig || defaultInvigilatorConfig,
    };
  }

  // 登录页面使用默认配置
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    examineeConfig: defaultExamineeConfig,
    invigilatorConfig: defaultInvigilatorConfig,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      // <Question key="doc" />,
      // <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: "https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png",
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>;
      },
    },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        // 获取当前页面信息用于重定向
        const { search, pathname } = location;
        const urlParams = new URL(window.location.href).searchParams;
        const searchParams = new URLSearchParams({
          redirect: pathname + search,
        });
        // 检查是否已有 redirect 参数
        const redirect = urlParams.get("redirect");
        // Note: There may be security issues, please note
        if (!redirect) {
          history.replace({
            pathname: loginPath,
            search: searchParams.toString(),
          });
        } else {
          history.push(loginPath);
        }
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    // links: isDev
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPI 文档</span>
    //       </Link>,
    //     ]
    //   : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) return <PageLoading />;
      return (
        <div
          style={{
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: 1,
              overflow: "auto",
            }}
          >
            {children}
          </div>
          {/* {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )} */}
        </div>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // baseURL: 'https://proapi.azurewebsites.net',
  // baseURL: "https://cd35ny570531.vicp.fun",

  baseURL: API_BASE_URL,
  ...errorConfig,
};
